import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronRight, X } from "lucide-react";

const BANNERS = [
  {
    id: "ksat",
    icon: "📝",
    text: "K-SAT 2026 Registrations Open · Get Scholarship Benefits",
    cta: "Register Now",
    target: "ksat-block",
  },
  {
    id: "results",
    icon: "🎉",
    text: "90%+ Toppers Every Year · Join Kothari Classes Today",
    cta: "Read Stories",
    target: "results-section",
  },
  {
    id: "offer",
    icon: "🔥",
    text: "Limited Time Admission Offer · Enroll Now",
    cta: "Enroll Now",
    target: "inquiry-form-section",
  },
];

export default function AnnouncementBar() {
  const [index, setIndex] = useState(0);
  const [closed, setClosed] = useState(false);
  const [paused, setPaused] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const timerRef = useRef(null);

  // Auto-rotate every 4s
  useEffect(() => {
    if (paused || closed) return;
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % BANNERS.length);
    }, 4000);
    return () => clearInterval(timerRef.current);
  }, [paused, closed]);

  const scrollTo = useCallback((targetId) => {
    const tryScroll = () => {
      const el = document.querySelector(`[data-testid="${targetId}"]`) || document.getElementById(targetId);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top, behavior: "smooth" });
        return true;
      }
      return false;
    };
    if (!tryScroll()) {
      // Element not on this page → go to admissions then scroll
      navigate("/admissions");
      setTimeout(tryScroll, 600);
    }
  }, [navigate]);

  const onClick = () => {
    const b = BANNERS[index];
    scrollTo(b.target);
  };

  if (closed) return null;

  return (
    <div
      className="bg-red-600 text-white relative overflow-hidden border-b border-red-700"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="announcement-bar"
      role="region"
      aria-label="Announcements"
    >
      {/* Subtle pattern */}
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent 0 10px, rgba(255,255,255,0.4) 10px 11px)" }} />

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 relative">
        <div className="h-12 sm:h-14 flex items-center justify-center gap-3 text-center">
          {/* Banner content */}
          <button
            onClick={onClick}
            data-testid={`announcement-${BANNERS[index].id}`}
            className="group flex items-center justify-center gap-2 sm:gap-3 flex-1 max-w-4xl text-[12px] sm:text-sm font-semibold tracking-tight focus:outline-none"
          >
            <span aria-hidden="true" className="text-base sm:text-lg shrink-0">{BANNERS[index].icon}</span>
            <span className="truncate sm:whitespace-normal">
              <span key={BANNERS[index].id} className="fade-up inline-block">
                {BANNERS[index].text}
              </span>
            </span>
            <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.16em] underline underline-offset-4 group-hover:no-underline whitespace-nowrap">
              {BANNERS[index].cta}
              <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </span>
          </button>

          {/* Dots */}
          <div className="hidden md:flex items-center gap-1.5 ml-2 shrink-0">
            {BANNERS.map((b, i) => (
              <button
                key={b.id}
                onClick={() => setIndex(i)}
                aria-label={`Go to banner ${i + 1}`}
                data-testid={`announcement-dot-${i}`}
                className={`h-1.5 transition-all ${i === index ? "w-6 bg-white" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
              />
            ))}
          </div>

          {/* Close */}
          <button
            onClick={() => setClosed(true)}
            aria-label="Dismiss announcement"
            data-testid="announcement-close"
            className="ml-2 shrink-0 p-1 hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
